import { Link } from "react-router-dom";
import Logo from "../assets/img/LogoSF.png";
import React, { useMemo, useEffect, useState, useCallback } from "react";
import {
  Sparkles,
  Target,
  UserPlus,
  LogIn,
  Users,
  ClipboardList,
  BarChart3,
  LineChart,
  Map,
  NotebookPen,
  CalendarDays,
  ShieldCheck,
} from "lucide-react";
import i18n from "../i18n";

// Mini hook i18n safe (évite TS2589)
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );

  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);

  // t change uniquement quand lng change
  const t = useCallback(
    (key: string, options?: Record<string, unknown>): string =>
      options != null
        ? String((i18n.t as any)(key, options))
        : String((i18n.t as any)(key)),
    []
  );

  return { t, lng };
}

export const Home = () => {
  const { t, lng } = useI18nSafe();

  const features = useMemo(() => {
    void lng;
    return [
      { icon: BarChart3, title: t("home:features.stats.title"), desc: t("home:features.stats.desc") },
      { icon: LineChart, title: t("home:features.advanced.title"), desc: t("home:features.advanced.desc") },
      { icon: Map, title: t("home:features.heatmap.title"), desc: t("home:features.heatmap.desc") },
      { icon: NotebookPen, title: t("home:features.playmaker.title"), desc: t("home:features.playmaker.desc") },
      { icon: CalendarDays, title: t("home:features.agenda.title"), desc: t("home:features.agenda.desc") },
      { icon: ShieldCheck, title: t("home:features.secure.title"), desc: t("home:features.secure.desc") },
    ];
  }, [lng, t]);

  const steps = useMemo(() => {
    void lng;
    return [
      { icon: UserPlus, title: t("home:steps.step1.title"), desc: t("home:steps.step1.desc") },
      { icon: Users, title: t("home:steps.step2.title"), desc: t("home:steps.step2.desc") },
      { icon: ClipboardList, title: t("home:steps.step3.title"), desc: t("home:steps.step3.desc") },
    ];
  }, [lng, t]);

  const year = new Date().getFullYear();

  return (
    <section className="relative w-full bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
      {/* Fond déco */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        {/* ✅ PATCH Capacitor: évite d'élargir le viewport */}
        <div className="absolute left-1/2 top-[-12rem] h-[28rem] w-[100vw] max-w-[60rem] -translate-x-1/2 bg-gradient-to-r from-orange-500/20 via-fuchsia-500/20 to-cyan-500/20 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(255,255,255,0.16),_transparent_60%)] dark:bg-[radial-gradient(ellipse_at_top,_rgba(255,255,255,0.06),_transparent_60%)]" />
      </div>

      {/* ✅ Layout: sur mobile on laisse scroller naturellement */}
      <div className="mx-auto w-full max-w-6xl px-4 pb-[calc(24px+env(safe-area-inset-bottom))] pt-6 sm:pt-10">
        <div className="w-full">
          {/* Carte hero */}
          <div className="mx-auto max-w-3xl rounded-2xl border border-white/40 bg-white/70 p-5 shadow-lg backdrop-blur-md dark:border-white/5 dark:bg-white/5 sm:p-8">
            <img
              src={Logo}
              alt={t("home:hero.logoAlt")}
              className="mx-auto mb-5 h-auto w-28 select-none rounded-lg drop-shadow-md dark:drop-shadow-none sm:mb-6 sm:w-48"
            />

            <h1 className="mb-2 text-center font-[Rubik] text-2xl font-bold italic text-orange-600 dark:text-orange-400 sm:mb-3 sm:text-4xl">
              {t("home:hero.title")}
            </h1>

            <p className="mb-2 flex items-center justify-center gap-2 text-center text-sm text-gray-700 dark:text-gray-300 sm:text-base">
              <Target className="h-5 w-5 opacity-80" />
              {t("home:hero.tagline")}
            </p>

            <p className="mx-auto max-w-xl text-center text-sm text-gray-600 dark:text-gray-300 sm:text-base">
              {t("home:hero.subtitle")}
            </p>

            {/* CTAs */}
            <div className="mt-5 flex flex-col items-stretch justify-center gap-3 sm:mt-6 sm:flex-row sm:items-center">
              <Link
                to="/signup"
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-orange-600 px-5 py-3 font-semibold text-white shadow-sm ring-1 ring-orange-700/20 transition-transform active:scale-[0.99] sm:w-auto sm:rounded-lg sm:py-2.5 hover:-translate-y-0.5 hover:bg-orange-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300 dark:ring-0"
                aria-label={t("home:cta.signup")}
              >
                <Sparkles className="h-5 w-5" />
                {t("home:cta.signup")}
              </Link>

              <Link
                to="/login"
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white/70 px-5 py-3 font-semibold text-gray-900 shadow-sm transition-transform active:scale-[0.99] sm:w-auto sm:rounded-lg sm:py-2.5 hover:-translate-y-0.5 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300 dark:border-white/10 dark:bg-white/5 dark:text-gray-100"
                aria-label={t("home:cta.login")}
              >
                <LogIn className="h-5 w-5" />
                {t("home:cta.login")}
              </Link>

              <Link
                to="/features"
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white/60 px-5 py-3 font-semibold text-gray-900/80 shadow-sm active:scale-[0.99] sm:w-auto sm:rounded-lg sm:border-transparent sm:bg-transparent sm:py-2.5 sm:shadow-none hover:underline dark:border-white/10 dark:bg-white/5 dark:text-gray-200 sm:dark:bg-transparent"
                aria-label={t("home:cta.features")}
              >
                {t("home:cta.features")}
              </Link>
            </div>

            {/* Badges valeur */}
            <ul className="mt-5 flex flex-wrap items-center justify-center gap-2 text-xs text-gray-600 dark:text-gray-400 sm:mt-6 sm:gap-3 sm:text-sm">
              <li className="rounded-full border border-gray-200 bg-white/70 px-3 py-1 dark:border-white/10 dark:bg-white/5">
                {t("home:badges.free")}
              </li>
              <li className="rounded-full border border-gray-200 bg-white/70 px-3 py-1 dark:border-white/10 dark:bg-white/5">
                {t("home:badges.dark")}
              </li>
              <li className="rounded-full border border-gray-200 bg-white/70 px-3 py-1 dark:border-white/10 dark:bg-white/5">
                {t("home:badges.responsive")}
              </li>
            </ul>
          </div>

          {/* Comment ça marche */}
          <div className="mx-auto mt-8 grid max-w-5xl grid-cols-1 gap-3 sm:mt-10 sm:grid-cols-3 sm:gap-4">
            {steps.map((s, i) => (
              <div
                key={i}
                className="rounded-2xl border border-gray-200 bg-white/80 p-4 shadow-sm dark:border-white/10 dark:bg-white/5 sm:rounded-xl sm:p-5"
              >
                <div className="mb-3 inline-flex rounded-xl border border-orange-200/60 bg-orange-50/70 p-2 dark:border-orange-300/20 dark:bg-orange-300/10 sm:rounded-lg">
                  <s.icon className="h-5 w-5 text-orange-600 dark:text-orange-300" />
                </div>
                <h3 className="mb-1 font-semibold">{s.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{s.desc}</p>
              </div>
            ))}
          </div>

          {/* Features */}
          <div className="mx-auto mt-8 grid max-w-5xl grid-cols-1 gap-3 sm:mt-10 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3">
            {features.map((f, i) => (
              <div
                key={i}
                className="group rounded-2xl border border-gray-200 bg-white/80 p-4 shadow-sm transition-all dark:border-white/10 dark:bg-white/5 sm:rounded-xl sm:p-5 hover:-translate-y-0.5 hover:shadow-md"
              >
                <div className="mb-3 inline-flex rounded-xl border border-orange-200/60 bg-orange-50/70 p-2 dark:border-orange-300/20 dark:bg-orange-300/10 sm:rounded-lg">
                  <f.icon className="h-5 w-5 text-orange-600 dark:text-orange-300" />
                </div>
                <h3 className="mb-1 font-semibold">{f.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{f.desc}</p>
              </div>
            ))}
          </div>

          {/* Actu */}
          <div className="mx-auto mt-8 max-w-5xl sm:mt-10">
            <div className="rounded-2xl border border-gray-200 bg-white/80 p-4 shadow-sm dark:border-white/10 dark:bg-white/5 sm:rounded-xl sm:p-5">
              <div className="flex items-center justify-between gap-3">
                <h4 className="text-base font-semibold">{t("home:news.title")}</h4>
                <Link
                  to="/news"
                  className="shrink-0 text-sm font-medium text-orange-600 hover:underline dark:text-orange-300"
                >
                  {t("home:news.all")}
                </Link>
              </div>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {t("home:news.body")}
              </p>
            </div>
          </div>

          {/* FAQ rapide */}
          <div className="mx-auto mt-8 max-w-3xl sm:mt-10">
            <h4 className="mb-3 text-center text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {t("home:faq.title")}
            </h4>
            <div className="space-y-2">
              <details className="rounded-2xl border border-gray-200 bg-white/80 p-4 open:shadow-sm dark:border-white/10 dark:bg-white/5 sm:rounded-lg">
                <summary className="cursor-pointer font-medium">{t("home:faq.q1.title")}</summary>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  {t("home:faq.q1.body")}
                </p>
              </details>
              <details className="rounded-2xl border border-gray-200 bg-white/80 p-4 open:shadow-sm dark:border-white/10 dark:bg-white/5 sm:rounded-lg">
                <summary className="cursor-pointer font-medium">{t("home:faq.q2.title")}</summary>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  {t("home:faq.q2.body")}
                </p>
              </details>
            </div>
          </div>

          {/* Footer */}
          <footer className="mx-auto mt-8 max-w-5xl pb-6 text-center text-xs text-gray-500 dark:text-gray-400 sm:mt-10 sm:pb-10">
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link to="/legal" className="hover:underline">{t("footer:links.legal")}</Link>
              <span>•</span>
              <Link to="/rules" className="hover:underline">{t("footer:links.terms")}</Link>
              <span>•</span>
              <a
                href="https://discord.gg/q5zR75uTB6"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                {t("footer:links.discord")}
              </a>
            </div>
            <p className="mt-2">
              {t("footer:bottom.copyright", { year, brand: "SwishData" })}
            </p>
          </footer>
        </div>
      </div>
    </section>
  );
};
